import { Component, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { ButtonToggle } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { CellHoverChangedEvent, RowClickEvent } from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	concat,
	debounceTime,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import {
	checkStringEquality,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from '../../../core/dx-types';
import {
	DxListsStorage,
	IDxList,
} from '../../../core/services/dx-lists.storage';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import {
	ISendEmailOutput,
	SendEmailPopupComponent,
} from '../../../ui/components/send-email-popup/send-email-popup.component';
import {
	createNewProject,
	getProjectElements,
	getProjectMembers,
	initProjects,
	isProjectDeletable,
	modifySearchTerm,
	navigateToOrphanProjects,
	navigateToProject,
	navigateToProjectEdit,
	orphanProjectsCheck,
	ProjectSelector,
	refreshProjects,
	resetProjectAfterDeletePopupClose,
	sendEmailToAdmin,
	setDefaultProject,
	setFavoriteProject,
	switchProjectsViewMode,
} from '../../store';
import {
	IProjectDeletePopupData,
	ProjectDeletePopupComponent,
} from '../project-delete-popup/project-delete-popup.component';
import { ProjectItemEntity } from './project-item.entity';
import { ProjectListEntity } from './project-list.entity';

type ProjectListOverlayTypes = {
	detailsPanelId: number | undefined;
	adminsPanelId: number | undefined;
	membersPanelId: number | undefined;
};

type ProjectAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};
@Component({
	selector: 'app-project-list',
	templateUrl: './project-list.component.html',
	styleUrls: ['./project-list.component.scss'],
	inputs: ['userIsAdmin'],
})
export class ProjectListComponent extends DcBaseComponent implements OnInit {
	public viewModes: Array<ButtonToggle> = [];
	public currentlyHovered = -1;
	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public accessedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public descriptionMaxCharCount = 200;
	public descriptionMaxLinesCount = 4;
	public labelMaxCharCount = 30;
	public labelMaxLinesCount = 1;
	public readonly menuRowActions: Array<ProjectAction> = [
		{
			actionName: 'edit',
			description: $localize`:i18n=@@general.action.edit:`,
			action: (item: unknown): void => {
				const project = item as ProjectItemEntity;
				this.navigateToProjectEditFromList(project.id);
			},
			icon: DcIcons.Settings,
		},
		{
			actionName: 'delete',
			description: $localize`:i18n=@@general.action.delete:`,
			action: (item: unknown): any => {
				const project = item as ProjectItemEntity;
				this.deleteProject(project);
			},
			icon: DcIcons.Delete,
		},
	];
	public readonly menuCardActions: Array<ProjectAction> = [
		{
			actionName: 'export project',
			description: $localize`:i18n=@@project.card.action.exportProject:`,
			action: (item: unknown): void => {},
			icon: ActionIcons.ExportDatablock,
		},
		{
			actionName: 'duplicate project',
			description: $localize`:i18n=@@project.card.action.duplicateProject:`,
			action: (item: unknown): void => {},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'import vesrion',
			description: $localize`:i18n=@@project.card.action.importVersion:`,
			action: (item: unknown): void => {},
			icon: ActionIcons.Import,
		},
		{
			actionName: 'duplicate version',
			description: $localize`:i18n=@@project.card.action.duplicateVersion:`,
			action: (item: unknown): void => {},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'delete',
			description: $localize`:i18n=@@general.action.delete:`,
			action: (item: unknown): any => {
				const project = item as ProjectItemEntity;
				this.deleteProject(project);
			},
			icon: DcIcons.Delete,
		},
	];
	public readonly menuCreateActions: Array<ProjectAction> = [
		{
			actionName: 'create',
			description: $localize`:i18n=@@project.list.action.new:`,
			action: (): Promise<void> => this.createProject(),
			icon: DcIcons.Project,
		},
		{
			actionName: 'import',
			description: $localize`:i18n=@@project.list.action.import:`,
			action: (): void => this.importProject(),
			icon: ActionIcons.Import,
		},
	];

	public vo$!: Observable<{
		items: ProjectListEntity;
		viewMode: string;
		openedOverlays: ProjectListOverlayTypes;
		searchTerm: string;
		isLoadingMembers: boolean;
		isAdmin: boolean;
		hasOrphanProjects: boolean;
	}>;
	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};
	protected readonly DcIcons = DcIcons;
	protected readonly ActionIcons = ActionIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DxOperations = DxOperations;
	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly openedOverlaySubject =
		new BehaviorSubject<ProjectListOverlayTypes>({
			detailsPanelId: undefined,
			adminsPanelId: undefined,
			membersPanelId: undefined,
		});
	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);
	private readonly searchTermSubject = new ReplaySubject<string>(1);
	private readonly projectsSubject = new ReplaySubject<ProjectListEntity>(1);

	private readonly searchTerm$ = this.searchTermSubject.asObservable().pipe(
		distinctUntilChanged(),
		debounceTime(320),
		withLatestFrom(this.projectSelector.getSearchTerm$()),
		tap(([currentValue, storeValue]) => {
			if (currentValue !== storeValue) {
				this.store.dispatch(
					modifySearchTerm({
						term: currentValue,
					})
				);
			}
		}),
		map(([currentValue]) => currentValue)
	);

	public constructor(
		private readonly store: Store,
		private readonly projectSelector: ProjectSelector,
		private readonly dxStorage: DxListsStorage,
		private readonly modalService: AppModalService
	) {
		super();
		this.cmpId = 'project-list';
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initProjects());

		const isAdmin$ = concat(
			of(false),
			this.toObservable<boolean>('userIsAdmin')
		).pipe(
			takeUntil(this.onDestroy$),
			tap((isAdmin) => {
				if (isAdmin) {
					this.store.dispatch(orphanProjectsCheck());
				}
			})
		);

		this.vo$ = combineAll({
			items: this.projectsSubject,
			viewMode: this.projectSelector.getProjectsViewMode$(),
			openedOverlays: this.openedOverlaySubject,
			searchTerm: this.searchTerm$,
			isLoadingMembers: this.projectSelector.getLoadingMembers$(),
			isAdmin: isAdmin$,
			hasOrphanProjects: this.projectSelector.hasOrphanProjects$(),
		});

		combineAll({
			projects: this.projectSelector.getProjectList$(),
			searchTerm: this.searchTerm$,
			viewMode: this.projectSelector.getProjectsViewMode$(),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projects, searchTerm, viewMode }) => {
					if (viewMode === 'CARD' && searchTerm) {
						this.projectsSubject.next(projects.filterProjects(searchTerm));
					} else {
						this.projectsSubject.next(projects);
					}
				})
			)
			.subscribe();

		this.projectSelector
			.getProjectList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.accessedByHeaderFilter = l
						.extractAccessors(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
				})
			)
			.subscribe();

		this.projectSelector
			.getSearchTerm$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((term) => this.searchTermSubject.next(term))
			)
			.subscribe();

		this.projectSelector
			.getProjectsViewMode$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((viewMode) => {
					this.viewModes = [
						{
							id: 'cards-view',
							label: $localize`:i18n=@@project.list.view.card:`,
							icon: DcIcons.CardView,
							isSelected: viewMode === 'CARD',
							cb: (): void =>
								this.store.dispatch(
									switchProjectsViewMode({
										viewMode: 'CARD',
									})
								),
						},
						{
							id: 'list-view',
							label: $localize`:i18n=@@project.list.view.list:`,
							icon: DcIcons.ListView,
							isSelected: viewMode === 'LIST',
							cb: (): void =>
								this.store.dispatch(
									switchProjectsViewMode({
										viewMode: 'LIST',
									})
								),
						},
					];
				})
			)
			.subscribe();
	}

	public navigateToProject(project: ProjectItemEntity): void {
		if (project.isExpired || !project.isActive) {
			return;
		}
		this.store.dispatch(
			navigateToProject({
				id: project.id,
			})
		);
	}

	public navigateToProjectEdit(event: Event, id: number): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToProjectEdit({
				id,
			})
		);
	}

	public setFavorite(event: Event, project: ProjectItemEntity): void {
		event.stopPropagation();
		if (project.isExpired || !project.isActive) {
			return;
		}
		this.store.dispatch(
			setFavoriteProject({
				id: project.id,
				isFavorite: !project.isFavorite,
			})
		);
	}

	public setDefault(event: Event, project: ProjectItemEntity): void {
		event.stopPropagation();
		if (project.isExpired || !project.isActive) {
			return;
		}
		this.store.dispatch(
			setDefaultProject({
				id: project.id,
				isDefault: !project.isDefault,
			})
		);
	}

	public refresh(): void {
		this.store.dispatch(refreshProjects());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			this.firstLoadSubject.next(false);
		}
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ProjectItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateAccessByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ProjectItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public openInfoOverlay(event: Event, id: number): void {
		event.stopPropagation();
		this.openedOverlaySubject.next({
			detailsPanelId: id,
			adminsPanelId: undefined,
			membersPanelId: undefined,
		});
	}

	public openMembersOverlay(event: Event, id: number): void {
		event.stopPropagation();
		this.openedOverlaySubject.next({
			detailsPanelId: undefined,
			adminsPanelId: undefined,
			membersPanelId: id,
		});
		this.store.dispatch(
			getProjectMembers({
				projectId: id,
			})
		);
	}

	public openAdminsOverlay(event: Event, id: number): void {
		event.stopPropagation();
		this.openedOverlaySubject.next({
			detailsPanelId: undefined,
			adminsPanelId: id,
			membersPanelId: undefined,
		});
		this.store.dispatch(
			getProjectMembers({
				projectId: id,
			})
		);
	}

	public closeOverlay(): void {
		this.openedOverlaySubject.next({
			detailsPanelId: undefined,
			adminsPanelId: undefined,
			membersPanelId: undefined,
		});
	}

	public searchProjectCards(term: string): void {
		this.searchTermSubject.next(term);
	}

	public rowClick(event: RowClickEvent): void {
		this.store.dispatch(
			navigateToProject({
				id: event.data.id,
			})
		);
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public cardMenuOpen($event: MouseEvent): void {
		$event.stopPropagation();
	}

	public async deleteProject(project: ProjectItemEntity): Promise<void> {
		this.store.dispatch(
			isProjectDeletable({
				id: project.id,
			})
		);
		const canBeDeleted = await firstValueFrom(
			this.projectSelector.getProject$(project.id).pipe(
				filter((project) => project.isDeletable !== undefined),
				map((p) => p.isDeletable)
			)
		);
		let updatedProject = project;
		if (canBeDeleted) {
			this.store.dispatch(
				getProjectElements({
					id: project.id,
				})
			);
			updatedProject = await firstValueFrom(
				this.projectSelector
					.getProject$(project.id)
					.pipe(filter((projects) => projects.elements !== undefined))
			);
		}
		const dialogRef = this.modalService.openPopup<IProjectDeletePopupData>(
			ProjectDeletePopupComponent,
			{
				title: $localize`:i18n=@@project.delete-popup.title:`,
				subTitle: project.label,
				project: updatedProject,
			},
			{
				disableClose: true,
			}
		);

		const diagData = await firstValueFrom(dialogRef.afterClosed());
		if (!diagData || !diagData.isDeleted) {
			this.store.dispatch(
				resetProjectAfterDeletePopupClose({
					id: project.id,
				})
			);
		}
	}

	public navigateToProjectEditFromList(id: number): void {
		this.store.dispatch(
			navigateToProjectEdit({
				id,
			})
		);
	}

	public async openSendEmailToAdminPopup(
		projectId: number,
		adminId: string
	): Promise<void> {
		const dialogRef = this.modalService.openPopup(
			SendEmailPopupComponent,
			{
				title: $localize`:i18n=@@project.send-email-popup.send-email:`,
				subTitle: '',
			},
			{
				disableClose: true,
			}
		);

		const diagData = await firstValueFrom(dialogRef.afterClosed());
		if (diagData) {
			const { title, message } = diagData as ISendEmailOutput;
			this.store.dispatch(
				sendEmailToAdmin({
					projectId,
					adminId,
					title,
					message,
				})
			);
		}
	}

	public async navigateToOrphanProjects(): Promise<void> {
		const isAdmin = await firstValueFrom(
			this.toObservable<boolean>('userIsAdmin')
		);
		if (isAdmin) {
			this.store.dispatch(navigateToOrphanProjects());
		}
	}

	public async createProject(): Promise<void> {
		const isAdmin = await firstValueFrom(
			this.toObservable<boolean>('userIsAdmin')
		);
		if (isAdmin) {
			this.store.dispatch(createNewProject());
		}
	}

	public importProject(): void {
		// TODO
	}
}
